import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Share, MoreVertical, Menu, SquarePlus, Copy, Download, Smartphone, Globe, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { AuthBackdrop, stagger, rise, spring } from "./bits";
import { LOGO_URL } from "@/i18n/translations";

// ---------- Install-state detection ----------
// True only when the app runs as an installed app (home-screen icon), never in a browser tab.
export const isStandaloneApp = () => {
  try {
    const mm = (q) => window.matchMedia && window.matchMedia(q).matches;
    if (mm("(display-mode: standalone)") || mm("(display-mode: fullscreen)") || mm("(display-mode: minimal-ui)")) return true;
    if (typeof window.navigator.standalone === "boolean" && window.navigator.standalone) return true; // iOS
    if (document.referrer.startsWith("android-app://")) return true; // TWA
  } catch { /* fall through */ }
  return false;
};

// ---------- OS / browser detection ----------
export const detectEnv = () => {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  let browser = "other";
  if (isIOS) {
    if (/CriOS/i.test(ua)) browser = "chrome";
    else if (/FxiOS/i.test(ua)) browser = "firefox";
    else if (/EdgiOS/i.test(ua)) browser = "edge";
    else browser = "safari"; // every iOS browser is WebKit — default to Safari instructions
  } else if (isAndroid) {
    if (/SamsungBrowser/i.test(ua)) browser = "samsung";
    else if (/EdgA\//i.test(ua)) browser = "edge";
    else if (/Firefox/i.test(ua)) browser = "firefox";
    else browser = "chrome"; // Chrome & others → Chrome instructions
  }
  return { isIOS, isAndroid, browser };
};

// ---------- Native install prompt (captured even before React mounts) ----------
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("cfm-install-available"));
  });
}

// Step definitions: translation key + icon per browser/os combo
const STEP_SETS = {
  "android-chrome": [
    { k: "install_chrome_1", icon: MoreVertical },
    { k: "install_chrome_2", icon: SquarePlus },
    { k: "install_chrome_3", icon: Download },
  ],
  "android-samsung": [
    { k: "install_samsung_1", icon: Menu },
    { k: "install_samsung_2", icon: SquarePlus },
    { k: "install_samsung_3", icon: Smartphone },
  ],
  "android-firefox": [
    { k: "install_firefox_1", icon: MoreVertical },
    { k: "install_firefox_2", icon: SquarePlus },
  ],
  "android-edge": [
    { k: "install_edge_1", icon: Menu },
    { k: "install_edge_2", icon: Smartphone },
  ],
  "ios-safari": [
    { k: "install_safari_1", icon: Share },
    { k: "install_safari_2", icon: SquarePlus },
    { k: "install_safari_3", icon: Check },
  ],
  other: [
    { k: "install_other_1", icon: Download },
    { k: "install_other_2", icon: Smartphone },
  ],
};

const BROWSER_KEYS = { chrome: "Chrome", samsung: "Samsung Internet", firefox: "Firefox", edge: "Edge", safari: "Safari", other: "Browser" };

// Full-screen install gate — the ONLY thing visible when the app runs inside a browser tab.
const InstallGate = () => {
  const { t, lang, changeLang } = useLang();
  const m = t.mapp;
  const env = detectEnv();
  const [canNative, setCanNative] = useState(!!deferredPrompt);

  useEffect(() => {
    const on = () => setCanNative(true);
    window.addEventListener("cfm-install-available", on);
    return () => window.removeEventListener("cfm-install-available", on);
  }, []);

  const installNow = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch { /* dismissed */ }
    deferredPrompt = null;
    setCanNative(false);
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/app`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(m.install_copied);
  };

  const iosNeedsSafari = env.isIOS && env.browser !== "safari";
  const setKey = iosNeedsSafari
    ? null
    : env.isIOS
      ? "ios-safari"
      : env.isAndroid
        ? `android-${env.browser}`
        : "other";
  const steps = setKey ? (STEP_SETS[setKey] || STEP_SETS.other) : [];

  return (
    <AuthBackdrop>
      <div className="flex-1 flex flex-col items-center px-6 py-10 min-h-screen overflow-y-auto" data-testid="install-gate">
        {/* language toggle — staff can't reach settings before installing */}
        <div className="w-full max-w-sm flex justify-end" data-testid="install-lang-toggle">
          <div className="flex rounded-full border border-brand/20 bg-white/80 overflow-hidden text-xs font-bold">
            {["el", "en"].map((l) => (
              <button
                key={l}
                onClick={() => changeLang(l)}
                className={`px-3 py-1.5 uppercase transition-colors ${lang === l ? "bg-brand text-brand-cream" : "text-brand-ink/50"}`}
                data-testid={`install-lang-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <motion.img
          src={LOGO_URL}
          alt="Care For Me"
          className="mt-8 h-14 object-contain"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        />
        <motion.h1
          className="mt-7 font-heading text-3xl font-medium text-brand-ink text-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          data-testid="install-title"
        >
          {m.install_title}
        </motion.h1>
        <motion.p
          className="mt-2 text-sm text-brand-muted text-center max-w-xs"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
        >
          {m.install_sub}
        </motion.p>

        {/* detected browser chip */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, ...spring }}
          className="mt-6 flex items-center gap-2 rounded-full bg-white border border-brand/15 px-4 py-2 shadow-[0_6px_20px_rgba(134,77,99,0.08)]"
          data-testid="install-browser-chip"
        >
          <Globe className="h-4 w-4 text-brand" />
          <span className="text-xs font-bold text-brand-ink/70">
            {m.install_detected}: {BROWSER_KEYS[env.browser]}
            {env.isIOS ? " · iPhone" : env.isAndroid ? " · Android" : ""}
          </span>
        </motion.div>

        <div className="mt-8 w-full max-w-sm">
          {iosNeedsSafari ? (
            // iPhone in Chrome/Firefox/Edge — Apple only allows installing via Safari
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, ...spring }}
              className="rounded-3xl bg-white border border-amber-300/60 p-6 text-center shadow-[0_12px_36px_rgba(134,77,99,0.12)]"
              data-testid="install-ios-safari-note"
            >
              <div className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-amber-100 text-amber-600">
                <Share className="h-6 w-6" />
              </div>
              <h2 className="mt-4 font-heading text-xl font-medium text-brand-ink">{m.install_ios_other_title}</h2>
              <p className="mt-2 text-sm text-brand-ink/70">{m.install_ios_other_text}</p>
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={copyLink}
                className="mt-5 w-full h-13 py-3.5 rounded-2xl bg-brand text-brand-cream font-bold flex items-center justify-center gap-2 shadow-[0_12px_30px_rgba(134,77,99,0.35)]"
                data-testid="install-copy-link"
              >
                <Copy className="h-5 w-5" />
                {m.install_copy_link}
              </motion.button>
            </motion.div>
          ) : (
            <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
              {/* one-tap native install where the browser allows it */}
              {canNative && (
                <motion.button
                  variants={rise}
                  whileTap={{ scale: 0.96 }}
                  onClick={installNow}
                  className="w-full h-14 rounded-2xl bg-brand text-brand-cream font-bold text-lg flex items-center justify-center gap-2 shadow-[0_12px_30px_rgba(134,77,99,0.35)]"
                  data-testid="install-now-btn"
                >
                  <Download className="h-5 w-5" />
                  {m.install_now}
                </motion.button>
              )}

              <motion.p variants={rise} className="pt-1 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-brand/60">
                {canNative ? m.install_or_steps : m.install_steps_title}
              </motion.p>

              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.k}
                    variants={rise}
                    className="flex items-center gap-4 rounded-2xl bg-white border border-brand/10 px-4 py-4 shadow-[0_6px_20px_rgba(134,77,99,0.07)]"
                    data-testid={`install-step-${i + 1}`}
                  >
                    <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full bg-brand-soft text-brand font-heading font-semibold text-lg">
                      {i + 1}
                    </span>
                    <p className="flex-1 text-sm font-medium text-brand-ink leading-snug">{m[s.k]}</p>
                    <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl border border-brand/15 bg-brand-cream text-brand">
                      <Icon className="h-5 w-5" />
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* after install */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="mt-8 flex items-start gap-3 rounded-2xl border border-dashed border-brand/25 bg-brand-soft/40 px-4 py-4"
            data-testid="install-after-note"
          >
            <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <p className="text-xs text-brand-ink/70 leading-relaxed">
              <span className="font-bold text-brand-ink">{m.install_after_title}: </span>
              {m.install_after_text}
            </p>
          </motion.div>
        </div>
      </div>
    </AuthBackdrop>
  );
};

export default InstallGate;
